import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { contractApi } from '@/features/contracts/api/contractApi';

/**
 * Hook pour récupérer les détails de paiement d'un contrat
 */
export const usePaymentDetails = (contractId: number | undefined) => {
  return useQuery({
    queryKey: ['payment-details', contractId],
    queryFn: () => contractApi.getPaymentDetails(contractId!),
    enabled: !!contractId,
    staleTime: 60000, // 1 minute — les paiements ne changent pas souvent
  });
};

/**
 * Hook pour télécharger la facture/échéancier d'un contrat.
 *
 * `isMonthly` conditionne le nom de fichier : l'ancien `echeancier-*.pdf` codé en dur
 * empêchait le vendeur de distinguer un échéancier d'une facture comptant.
 */
export const useDownloadInvoice = (
  contractId: number | undefined,
  contractLabel: string | undefined,
  isMonthly: boolean,
) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const download = useCallback(async () => {
    if (!contractId) return;
    setIsDownloading(true);
    try {
      // Le try n'englobe QUE l'appel réseau : une erreur du code DOM ci-dessous n'a rien à
      // voir avec la disponibilité de la facture et ne doit pas afficher « facture
      // indisponible ».
      let blob: Blob;
      try {
        blob = await contractApi.downloadInvoice(contractId);
      } catch (err: any) {
        throw await toDownloadError(err);
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${isMonthly ? 'echeancier' : 'facture'}-${contractLabel || contractId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      // Ne PAS avaler l'erreur : un 409 signale un contrat en mode « factures Stripe »
      // pour lequel aucun échéancier ne doit être généré (garde de mode côté API).
      toast.error(err?.message || 'Impossible de télécharger la facture pour ce contrat');
    } finally {
      setIsDownloading(false);
    }
  }, [contractId, contractLabel, isMonthly]);

  return { download, isDownloading };
};

/**
 * Extrait le message d'erreur du back.
 *
 * La requête étant en `responseType: 'blob'`, un corps d'erreur JSON arrive lui aussi sous
 * forme de Blob : sans relecture, le message du 409 (« ce contrat est en mode factures
 * Stripe, aucun échéancier à générer ») serait perdu au profit d'un message générique.
 */
async function toDownloadError(err: any): Promise<Error> {
  const payload = err?.response?.data;
  if (payload instanceof Blob) {
    try {
      const parsed = JSON.parse(await payload.text());
      if (parsed?.message) return new Error(parsed.message);
    } catch {
      // Corps non-JSON (PDF tronqué, HTML d'erreur proxy…) : message générique
    }
  } else if (payload?.message) {
    return new Error(payload.message);
  }
  return new Error('Impossible de télécharger la facture pour ce contrat');
}

/**
 * Hook pour télécharger le mandat de prélèvement SEPA client d'un contrat
 */
export const useDownloadSepaMandate = (contractId: number | undefined, contractLabel?: string) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const download = useCallback(async () => {
    if (!contractId) return;
    setIsDownloading(true);
    try {
      const blob = await contractApi.downloadSepaMandate(contractId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mandat-sepa-${contractLabel || contractId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Impossible de télécharger le mandat SEPA');
    } finally {
      setIsDownloading(false);
    }
  }, [contractId, contractLabel]);

  return { download, isDownloading };
};
